import classNames from 'classnames';
import React, { ReactNode, JSX } from 'react';

import { IconButton } from '@eika/button';
import '@eika/card-style/src/TipsCard.scss';
import { Icon, IconTypes } from '@eika/icon';
import { Label, LabelTypes } from '@eika/label';

type DataTrackLinknameProps = {
    link?: string;
    closebutton?: string;
};

export interface TipsCardProps {
    // @default 'attention'
    labelType?: LabelTypes;
    label: string;
    title: string;
    text: React.ReactNode;
    link?: string;
    className?: string;
    icon?: IconTypes;
    onClose?: () => void;
    button?: React.ReactNode;
    dataTrackId?: string;
    dataTrackLinkname?: DataTrackLinknameProps;
}

export interface TipsCardWrapperProps {
    children: ReactNode;
    className?: string;
    link: string | undefined;
    onClose?: () => void;
    dataTrackId?: string;
    dataTrackLinkname?: DataTrackLinknameProps;
}

interface CloseTipsCardIconButtonProps {
    onClose: () => void;
    dataTrackId?: string;
    dataTrackLinkname?: DataTrackLinknameProps;
}

const CloseTipsCardIconButton = ({
    onClose,
    dataTrackId,
    dataTrackLinkname,
}: CloseTipsCardIconButtonProps): JSX.Element => (
    <IconButton
        screenReaderText="Lukk"
        className="sgw-tips-card__close-button"
        icon={IconTypes.KRYSS_24}
        data-track-id={dataTrackId}
        data-track-linkname={dataTrackLinkname?.closebutton}
        onClick={(event) => {
            event.stopPropagation();
            onClose();
        }}
    />
);

const TipsCardWrapper = ({
    link,
    className,
    children,
    onClose,
    dataTrackId,
    dataTrackLinkname,
}: TipsCardWrapperProps): JSX.Element => {
    return link ? (
        <div className={classNames('sgw-tips-card__wrapper with-link')}>
            <a
                href={link}
                className={classNames('sgw-tips-card', className)}
                data-track-id={dataTrackId}
                data-track-linkname={dataTrackLinkname?.link}
            >
                {children}
            </a>
            {onClose && (
                <CloseTipsCardIconButton
                    onClose={onClose}
                    dataTrackId={dataTrackId}
                    dataTrackLinkname={dataTrackLinkname}
                />
            )}
        </div>
    ) : (
        <div className="sgw-tips-card__wrapper">
            {onClose && (
                <CloseTipsCardIconButton
                    onClose={onClose}
                    dataTrackId={dataTrackId}
                    dataTrackLinkname={dataTrackLinkname}
                />
            )}
            <div className={classNames('sgw-tips-card', className)}>{children}</div>
        </div>
    );
};

const TipsCard = ({
    labelType = 'attention',
    label = 'Tips',
    title,
    text,
    className,
    link,
    icon = IconTypes.LYSPAERE_24,
    onClose,
    button,
    dataTrackId,
    dataTrackLinkname,
}: TipsCardProps): JSX.Element => {
    return (
        <div className={classNames('sgw-tips-card__container', className)}>
            <Label type={labelType} className="sgw-tips-card__label" text={label} icon={icon} />
            <TipsCardWrapper
                link={link}
                className={className}
                onClose={onClose}
                dataTrackId={dataTrackId}
                dataTrackLinkname={dataTrackLinkname}
            >
                <h2 className="sgw-tips-card__title">{title}</h2>
                {text && <p>{text}</p>}
                {button ? (
                    <div className="sgw-tips-card__button">{button}</div>
                ) : (
                    link && <Icon type={IconTypes.PIL_HOEYRE} />
                )}
            </TipsCardWrapper>
        </div>
    );
};

export default TipsCard;
